import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import throttle from 'lodash/throttle';
import '../../styles/pages/DestinationPage/OptionSection.css';
import '../../styles/components/blog_page/BlogSection.css';
import { FaEye, FaThumbsUp, FaThumbsDown, FaUser, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { format } from 'date-fns';
import usePagination from '../../components/common/usePagination';
import SearchBar from '../../components/destination_page/SearchBar';

const BlogSection = ({ type = 'blog', origin_id = null, destination, showCreateButtons, userSpecific = false }) => {
  const navigate = useNavigate();
  const guidesCacheRef = useRef({});
  const [searchTerm, setSearchTerm] = useState('');
  const { items, orderBy, loading, loadingMore, isRefreshing, error, nextCursor, handleOrderChange, handleLoadMore, fetchItems } = usePagination(
    '/guides/summary',
    'newest',
    guidesCacheRef,
    origin_id,
    type,
    userSpecific
  );
  const throttledHandleOrderChangeRef = useRef(null);
  useEffect(() => {
    throttledHandleOrderChangeRef.current = throttle((newOrderBy) => {
      handleOrderChange(newOrderBy);
      fetchItems(null, false, searchTerm, newOrderBy);
    }, 500, { leading: true, trailing: false });
  }, [handleOrderChange, fetchItems, searchTerm]);
  const handleOrderChangeWrapper = (e) => {
    throttledHandleOrderChangeRef.current(e.target.value);
  };
  const throttledHandleLoadMoreRef = useRef(null);
  useEffect(() => {
    throttledHandleLoadMoreRef.current = throttle(() => {
      handleLoadMore();
    }, 1000, { leading: true, trailing: false });
    return () => {
      throttledHandleLoadMoreRef.current.cancel();
    };
  }, [handleLoadMore]);
  const [loadMoreTriggered, setLoadMoreTriggered] = useState(false);
  const prevItemsCountRef = useRef(items.length);
  const itemsRef = useRef([]);
  const handleLoadMoreClick = () => {
    setLoadMoreTriggered(true);
    throttledHandleLoadMoreRef.current();
  };
  useEffect(() => {
    if (loadMoreTriggered && items.length > prevItemsCountRef.current) {
      const newItem = itemsRef.current[prevItemsCountRef.current];
      if (newItem) {
        newItem.scrollIntoView({ behavior: 'smooth' });
      }
      setLoadMoreTriggered(false);
    }
    prevItemsCountRef.current = items.length;
  }, [items, loadMoreTriggered]);
  const handleCreateBlog = () => {
    navigate(`/admin/create-blog/editor/${encodeURIComponent(origin_id || '')}`, {
      state: { originName: destination ? destination.name : 'General' }
    });
  };
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value.slice(0, 50));
  };
  const handleSearchClick = () => {
    fetchItems(null, false, searchTerm, orderBy);
  };
  return (
    <section className="option-section blog-section">
      <h2>Blogs</h2>
      <div className="option-section-search-and-order">
        <SearchBar searchTerm={searchTerm} onSearchTermChange={handleSearchInputChange} onSearchClick={handleSearchClick} disabled={loading || isRefreshing} />
        <div className="option-section-order">
          <label htmlFor="order-select">Order by:</label>
          <select id="order-select" value={orderBy} onChange={handleOrderChangeWrapper} disabled={loading || isRefreshing}>
            <option value="newest">Newest</option>
            <option value="most_viewed">Most Viewed</option>
            <option value="most_liked">Most Liked</option>
          </select>
        </div>
      </div>
      {loading && items.length === 0 ? (
        <div className="loading-message" role="status" aria-live="polite">Loading blogs...</div>
      ) : error ? (
        <div className="error-message" role="alert">{error}</div>
      ) : items.length === 0 ? (
        <div className="loading-message" role="status" aria-live="polite">No blogs available.</div>
      ) : (
        <>
          <ul>
            {items.map((guide, index) => (
              <li key={guide.guide_id} ref={el => itemsRef.current[index] = el} className="animated-list-item" style={{ animationDelay: `${index * 100}ms` }}>
                <Link
                  to={guide.is_public ? `/guide/${encodeURIComponent(guide.origin_id)}/${guide.guide_id}` : `/guide/${encodeURIComponent(guide.origin_id)}/edit/${guide.guide_id}`}
                  className="option-link"
                  aria-label={`View blog titled ${guide.title}`}
                  state={{ originName: guide.origin_name }}
                >
                  <div className="list-item">
                    <div className="guide-thumbnail-container">
                      {guide.introduction_thumbnail_url ? (
                        <img
                          src={guide.introduction_thumbnail_url}
                          alt={`${guide.title} Thumbnail`}
                          className="guide-thumbnail"
                          loading="lazy"
                        />
                      ) : (
                        <div className="default-thumbnail"></div>
                      )}
                    </div>
                    <div className="list-item-content">
                      <h3>{guide.title}</h3>
                      <div className="card-footer">
                        <p className="option-author">
                          <FaUser className="option-stat-icon" />
                          {guide.author_name || 'Unknown Author'}
                        </p>
                        <p className="option-origin">
                          <FaMapMarkerAlt className="option-stat-icon" />
                          {guide.origin_name || 'N/A'}
                        </p>
                        <p className="option-updated-date">
                          <FaCalendarAlt className="option-stat-icon" />
                          {new Date(guide.updated_datetime_utc).toLocaleDateString()}
                        </p>
                        <div className="option-stats">
                          <span>
                            <FaEye className="option-stat-icon" /> {guide.view_count || 0}
                          </span>
                          <span>
                            <FaThumbsUp className="option-stat-icon" /> {guide.like_count || 0}
                          </span>
                          <span>
                            <FaThumbsDown className="option-stat-icon" /> {guide.dislike_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {nextCursor && (
            <button onClick={handleLoadMoreClick} className="load-more-button" disabled={loadingMore || isRefreshing} aria-label="Load more blogs">
              {loadingMore ? (
                <>
                  <span className="loading-spinner"></span> Loading...
                </>
              ) : (
                'Load More'
              )}
            </button>
          )}
          {isRefreshing && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
            </div>
          )}
        </>
      )}
      {showCreateButtons && (
        <button onClick={handleCreateBlog} className="cta-button">Write a Blog</button>
      )}
    </section>
  );
};

export default BlogSection;
